import cx from "clsx";
import { UserId } from "kreate-common/modules/business-types";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import React from "react";

import SectionBanner from "./containers/SectionBanner";
import PageControlV2 from "./containers/SectionBody/components/PageControlV2";
import SectionArtworks from "./containers/SectionBody/containers/SectionArtworks";
import SectionCollections from "./containers/SectionBody/containers/SectionCollections";
import TabAbout from "./containers/SectionBody/containers/TabAbout";
import TabPayouts from "./containers/SectionBody/containers/TabPayouts";
import SectionHead from "./containers/SectionHead";
import styles from "./index.module.scss";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import ErrorViewer from "@/modules/kreate-components/components/ErrorViewer";
import { getThumbnailUrlFromBlobId } from "@/modules/kreate-components/components/ImageViewer/utils";
import PageLayout from "@/modules/kreate-components/components/PageLayout";
import { useUser } from "@/modules/resources/hooks/useUser";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  userId: UserId;
};

export default function PageProfile({ className, style, userId }: Props) {
  const [user, userError] = useUser(userId);
  const [activeTab, setActiveTab] = React.useState("gallery");
  const loginStatus = useLoginStatus();

  const isOwner =
    loginStatus?.loggedIn && user && loginStatus.userId === user?.userId;

  if (userError) {
    return (
      <div className={cx(styles.container, className)} style={style}>
        <ErrorViewer error={userError} />
      </div>
    );
  }

  return (
    <PageLayout
      className={cx(styles.container, className)}
      style={style}
      background="light"
      title={`${user?.displayName || userId} | Kreate`}
      description={user?.headline || undefined}
      // Product question: Avatar or banner here?
      imageUrl={
        user?.banner
          ? getThumbnailUrlFromBlobId(user.banner.blob.blobId)
          : undefined
      }
    >
      <SectionBanner banner={user?.banner} />
      <SectionHead user={user} />
      <PageControlV2.Root value={activeTab} onChange={setActiveTab}>
        <PageLayout.Divider />
        <PageLayout.Section>
          <PageControlV2.List
            options={[
              { value: "gallery", label: "Gallery" },
              { value: "about", label: "About" },
              {
                value: "payouts",
                label: "Payouts",
                /**NOTE: 10/8/2023: accept agents only */
                hidden: !isOwner || !user.acceptAgents,
              },
            ]}
          />
        </PageLayout.Section>
        <PageLayout.Divider />
        <PageControlV2.Page value="gallery">
          {user?.collections.length || isOwner ? (
            <>
              <SectionCollections
                style={{ padding: "56px 0 48px 0" }}
                user={user}
              />
              <PageLayout.Section>
                <Divider.Horizontal />
              </PageLayout.Section>
            </>
          ) : null}
          <PageLayout.Section>
            <SectionArtworks user={user} key={user?.userId} />
          </PageLayout.Section>
        </PageControlV2.Page>
        <PageControlV2.Page value="about">
          <PageLayout.Section>
            <TabAbout user={user} />
          </PageLayout.Section>
        </PageControlV2.Page>
        <PageControlV2.Page value="payouts">
          <PageLayout.Section>
            <TabPayouts payoutMethods={user?.payoutMethods} />
          </PageLayout.Section>
        </PageControlV2.Page>
      </PageControlV2.Root>
      <PageLayout.Divider />
    </PageLayout>
  );
}
